import { cn } from "@/lib/utils";

const SIZE = {
  sm: { box: 16, stroke: 2.25 },
  md: { box: 28, stroke: 2.75 },
  lg: { box: 44, stroke: 3.25 },
} as const;

export type ProgressRingSize = keyof typeof SIZE;

export type ProgressRingProps = {
  /** 0–100 for determinate; omit / null for indeterminate spin. */
  value?: number | null;
  size?: ProgressRingSize;
  className?: string;
  trackClassName?: string;
  indicatorClassName?: string;
  label?: string;
  /** Visually hide the label while keeping it for screen readers. */
  labelSrOnly?: boolean;
};

/**
 * Circular progress indicator — indeterminate (route/fetch) or determinate (0–100).
 * Spin uses plain CSS (@keyframes tb-progress-ring-spin) so it stays smooth
 * even when global reduced-motion shortens other transitions.
 */
export function ProgressRing({
  value = null,
  size = "md",
  className,
  trackClassName,
  indicatorClassName,
  label = "Loading",
  labelSrOnly = true,
}: ProgressRingProps) {
  const { box, stroke } = SIZE[size];
  const radius = (box - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const determinate =
    typeof value === "number" && Number.isFinite(value)
      ? Math.min(100, Math.max(0, value))
      : null;
  const indeterminate = determinate == null;
  const dash =
    determinate == null
      ? undefined
      : `${(determinate / 100) * circumference} ${circumference}`;

  return (
    <span
      className={cn("progress-ring", className)}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={determinate ?? undefined}
      aria-busy={indeterminate ? true : undefined}
      data-testid="progress-ring"
      data-state={indeterminate ? "indeterminate" : "determinate"}
    >
      <svg
        width={box}
        height={box}
        viewBox={`0 0 ${box} ${box}`}
        className={cn(
          "progress-ring__svg",
          indeterminate && "progress-ring__svg--spin",
        )}
        aria-hidden
      >
        <circle
          className={cn("progress-ring__track", trackClassName)}
          cx={box / 2}
          cy={box / 2}
          r={radius}
          strokeWidth={stroke}
        />
        <circle
          className={cn(
            "progress-ring__indicator",
            indeterminate && "progress-ring__indicator--indeterminate",
            !indeterminate &&
              "transition-[stroke-dasharray] duration-standard ease-standard",
            indicatorClassName,
          )}
          cx={box / 2}
          cy={box / 2}
          r={radius}
          strokeWidth={stroke}
          pathLength={indeterminate ? 1 : undefined}
          strokeDasharray={
            indeterminate ? undefined : (dash ?? circumference)
          }
          strokeDashoffset={indeterminate ? undefined : 0}
          style={
            indeterminate
              ? undefined
              : {
                  strokeDasharray: dash,
                }
          }
        />
      </svg>
      <span
        className={cn(
          labelSrOnly ? "sr-only" : "ml-2 text-sm text-text-muted",
        )}
      >
        {label}
      </span>
    </span>
  );
}
